import { useEffect, useState, useContext, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { toast } from 'react-toastify'
import {
  CTA_CTE,
  ENVIO_A_CARGO_COMPRADOR,
  ENVIO_GRATIS,
  MERCADO_PAGO,
  RETIRA_COMPRADOR
} from '../../constants/mediosEnviosPagos.js'
import {
  ID_ESTADO_MONTOS_OK,
  ID_ESTADO_MONTOS_NO_OK,
  ID_ESTADO_PENDIENTE_PAGO,
  ID_ESTADO_PEDIDO_CREADO,
  ID_ESTADO_NO_CONFIRMADO
} from '../../constants/pedidoEstados.js'
import { puedeAgregarDomicilio } from '../../helpers/domicilioPermisos.js'
import {
  normalizarMediosPago,
  opcionesMedioPago,
  requiereNormalizarMediosPago
} from '../../helpers/mediosPago.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useAuth } from '../../hooks/useAuth.js'
import { CarritoContext } from '../context/carritoContext.js'
import {
  calcularTotalItemConIva,
  calcularTotalItemSinIva,
  esMayorista,
  valorUnitarioSinIva
} from '../helpers/calcularPrecioTotal.js'
import { useTieneCuentaMPVinculada } from '../hooks/useMercadoPago.js'
import ModalDomicilio from '../perfil/ModalDomicilio.jsx'
import Precios from '../reutilizables/precios/Precios.jsx'
import * as carritoService from '../servicios/carritoService.js'
import * as compraService from '../servicios/compraService.js'
import { getCuentaCorrienteHabilitacion } from '../servicios/cuentaCorrienteService.js'
import * as datosFiscalesService from '../servicios/datosFiscalesService.js'
import * as domiciliosService from '../servicios/domiciliosService.js'
import * as fetchProvinciasAPI from '../servicios/fetchProvinciasAPI.js'
import { insertarMail } from '../servicios/mailsServices.js'
import * as mensajesService from '../servicios/mensajesService.js'
import * as publicacionesService from '../servicios/publicacionesService.js'
import styles from './Compra.module.css'
import Comprobante from './Comprobante.jsx'
import { DetallePrecio } from './DetallePrecio.jsx'
import { useListaEnvio } from './hooks/useListaEnvio.js'
import { ALIAS_CABA, ID_CABA, usePercepciones } from './hooks/usePercepciones.js'

const NOT_AUTHORIZED = 401
const PAGO_EN_CTA_CTE = 3
const PAGO_PENDIENTE = 0

const idProvinciaValida = (idProvincia) =>
  idProvincia !== null &&
  idProvincia !== undefined &&
  String(idProvincia).trim() !== '' &&
  !Number.isNaN(Number(idProvincia))

const obtenerIdItem = (item) => item.idPresupuestoItem || item.idPublicacion

const selectMenuPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 })
}

const Compra = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const vendedor = location.state?.vendedor

  const [domicilios, setDomicilios] = useState([])
  const [verComprobante, setVerComprobante] = useState(false)
  const [pedidos, setPedidos] = useState([])
  const [idMedioEnvio, setIdMedioEnvio] = useState({})
  const [catalogoMediosPago, setCatalogoMediosPago] = useState([])
  const [medioPagoSeleccionado, setMedioPagoSeleccionado] = useState({})
  const [verModalNuevoDomicilio, setVerModalNuevoDomicilio] = useState(false)
  const [provinciasAPI, setProvinciasAPI] = useState([])
  const [domiciliosVendedor, setDomiciliosVendedor] = useState([])
  const [direccionEntrega, setDireccionEntrega] = useState({})
  const [error, setError] = useState('')

  const { setCantidad, setItems } = useContext(CarritoContext)
  const { loginDataContext: { idUsuario, tipoUsuario, tieneWFCompra, empid } = {} } = useAuth()

  const { respuesta } = useTieneCuentaMPVinculada(vendedor?.empidVendedor)
  const pedidoCompletoEsCtaCte = useMemo(() => {
    const ids = (vendedor?.pedidoItems ?? []).map((i) => obtenerIdItem(i))

    const todosSeleccionados = ids.every(
      (id) => medioPagoSeleccionado[id] !== undefined && medioPagoSeleccionado[id] !== null
    )

    if (!todosSeleccionados) {
      return false
    }
    return ids.every((id) => Number(medioPagoSeleccionado[id]) === CTA_CTE)
  }, [medioPagoSeleccionado, vendedor?.pedidoItems])

  useEffect(() => {
    if (!vendedor) {
      navigate('/Compras', { replace: true })
    }
  }, [vendedor, navigate])

  const handleAbrirModalNuevoDomicilio = () => {
    setVerModalNuevoDomicilio(true)
  }

  const handleDomicilioAgregado = (domicilioInsertado) => {
    setDomicilios((prev) => [...prev, domicilioInsertado])
    toast('Domicilio agregado exitosamente')
  }

  const filtrarCuentaCorriente = async (mediosPagos, empidVendedor, empidComprador) => {
    // Filtrar cuenta corriente si el vendedor no la habilitó para este comprador
    const tieneCuentaCorriente = mediosPagos.some((m) => m.idMedioPago === CTA_CTE)
    if (tieneCuentaCorriente) {
      try {
        const { data: habilitacion } = await getCuentaCorrienteHabilitacion(
          empidVendedor,
          empidComprador
        )
        if (!habilitacion?.body?.habilita) {
          return mediosPagos.filter((m) => m.idMedioPago !== CTA_CTE)
        }
      } catch {
        // Si falla la consulta, se omite la cuenta corriente por seguridad
        return mediosPagos.filter((m) => m.idMedioPago !== CTA_CTE)
      }
    }
    return mediosPagos
  }

  /**
   * Los presupuestos llegan del backend .NET con los medios de pago como ids en texto, sin nombre
   * ni `habilitado`. En ese caso hay que buscar el catálogo y filtrarlo acá para después
   * normalizarlo a la forma que devuelve node (ver `mediosPagosPorItem`).
   */
  const obtenerMediosDePago = async () => {
    const { data } = await publicacionesService.BuscarMediosDePago(0)

    const idsMediosPagoItems = vendedor.pedidoItems.flatMap((item) => item.mediosPagos)

    let mediosPagoValidos = data.filter((m) =>
      idsMediosPagoItems.includes(m.idMedioPago.toString())
    )

    if (respuesta === NOT_AUTHORIZED) {
      mediosPagoValidos = mediosPagoValidos.filter((m) => m.idMedioPago !== MERCADO_PAGO)
    }
    const mediosPagoFiltrados = await filtrarCuentaCorriente(
      mediosPagoValidos,
      vendedor.empidVendedor,
      empid
    )
    setCatalogoMediosPago(mediosPagoFiltrados)
  }

  // Medios de pago de cada ítem en la forma de node, sin importar de qué backend salieron.
  const mediosPagosPorItem = useMemo(() => {
    const entradas = (vendedor?.pedidoItems ?? []).map((item) => [
      item.idPresupuestoItem || item.idPublicacion,
      normalizarMediosPago(item.mediosPagos, catalogoMediosPago)
    ])

    return new Map(entradas)
  }, [vendedor?.pedidoItems, catalogoMediosPago])

  const {
    mediosEnvios,
    listaEnvioSeleccionada,
    handleClickListaEnvio: handleClickListaEnvioBase,
    resetListaEnvioSeleccionada,
    obtenerPrecioEnvioPorItem,
    detectarConflictoListasPorDomicilio,
    idsItemConConflictoListas,
    obtenerEstadoCelda,
    costoEnvio
  } = useListaEnvio({
    idVendedor: vendedor?.idVendedor,
    pedidoItems: vendedor?.pedidoItems,
    idMedioEnvio,
    direccionEntrega,
    medioPagoSeleccionado
  })

  const obtenerProvincias = async () => {
    const { data: provinciasAPI } = await fetchProvinciasAPI.buscarProvinciasEnEBM()
    setProvinciasAPI(provinciasAPI)
  }

  const obtenerDomicilios = async (idUsuario) => {
    const { data: domTemp } = await domiciliosService.buscarTodas(idUsuario, tipoUsuario)
    if (domTemp.length === 0) {
      navigate('/AdministrarDomicilios')
    } else {
      setDomicilios(domTemp)
    }
  }

  useEffect(() => {
    if (!vendedor) return

    const hayMediosPagoSinResolver = (vendedor.pedidoItems ?? []).some((item) =>
      requiereNormalizarMediosPago(item.mediosPagos)
    )

    if (respuesta !== null && hayMediosPagoSinResolver) {
      obtenerMediosDePago()
    }
    obtenerProvincias()
  }, [idUsuario, respuesta])

  useEffect(() => {
    if (idUsuario) {
      obtenerDomicilios(idUsuario)
    }
  }, [idUsuario, domicilios.length])

  const handleClickMedioEnvio = (idMedioEnvio, idPublicacion) => {
    if (idMedioEnvio === RETIRA_COMPRADOR) {
      setDireccionEntrega((prev) => {
        const nuevo = { ...(prev || {}) }
        delete nuevo[idPublicacion]
        return nuevo
      })
    }

    setIdMedioEnvio((prev) => ({
      ...prev,
      [idPublicacion]: idMedioEnvio
    }))
    setError('')
  }

  const normalizarNombre = (provincia) =>
    (provincia ?? '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD') //Separa caracteres acentuados en 2 partes (letra base + tilde)
      .replace(/[\u0300-\u036f]/g, '') //Elmina las tildes del paso anterior
      .replace(/\./g, '') //Elimina puntos (EJ: C.A.B.A)
      .replace(/\s+/g, ' ')

  const provinciasPorNombre = useMemo(() => {
    const map = (provinciasAPI ?? []).reduce((acc, p) => {
      acc.set(normalizarNombre(p.nombre), Number(p.idProvincia))
      return acc
    }, new Map())

    ALIAS_CABA.forEach((a) => map.set(a, ID_CABA))

    return map
  }, [provinciasAPI])

  const provinciasPorId = useMemo(() => {
    const map = (provinciasAPI ?? []).reduce((acc, p) => {
      acc.set(Number(p.idProvincia), p.nombre)
      return acc
    }, new Map())
    return map
  }, [provinciasAPI])

  const handleClickDomicilioEnvio = (selected, idPublicacion) => {
    if (!selected) {
      setError('Debes seleccionar un domicilio de envío')
      return
    }
    const domicilioSeleccionado = domicilios.find((d) => d.idUsuarioDomicilio === selected.value)
    if (!domicilioSeleccionado) {
      setError('Domicilio no encontrado')
      return
    }

    const idProvinciaFinal = idProvinciaValida(domicilioSeleccionado.idProvincia)
      ? Number(domicilioSeleccionado.idProvincia)
      : (provinciasPorNombre.get(normalizarNombre(domicilioSeleccionado.provincia)) ?? 0)

    setDireccionEntrega((prev) => ({
      ...prev,
      [idPublicacion]: {
        idUsuarioDomicilio: domicilioSeleccionado.idUsuarioDomicilio,
        idProvincia: idProvinciaFinal,
        provincia: domicilioSeleccionado.provincia,
        idLocalidad: domicilioSeleccionado.idLocalidad,
        localidad: domicilioSeleccionado.localidad,
        cp: domicilioSeleccionado.cp,
        calle: domicilioSeleccionado.calle,
        calleAltura: domicilioSeleccionado.calleAltura,
        piso: domicilioSeleccionado.piso,
        departamento: domicilioSeleccionado.departamento
      }
    }))

    setError('')
  }

  const handleClickListaEnvio = (lista, idPublicacion) => {
    handleClickListaEnvioBase(lista, idPublicacion)
    setError('')
  }

  const crearPedido = async (checkoutsSeparados, conObservacion) => {
    try {
      const pedidosCreados = []
      for (const checkout of checkoutsSeparados) {
        const {
          checkoutDTO,
          percepciones: percepcionesDelPedido,
          requiereEnvioAConsultar
        } = checkout

        const esPedidoCtaCte = checkoutDTO.pedidoItems[0]?.idMedioPago === CTA_CTE
        const requiereImpuestos =
          !esPedidoCtaCte && (conObservacion || percepcionesDelPedido.length > 0)
        const requiereAvisoVendedor = requiereImpuestos || requiereEnvioAConsultar
        const { data: rtn } = await compraService.crearPedido(checkoutDTO)

        if (rtn.idPedido > 0) {
          pedidosCreados.push(rtn)
          let texto = ''

          if (percepcionesDelPedido.length > 0 && !esPedidoCtaCte) {
            await guardarPercepciones(rtn, percepcionesDelPedido)
          }
          const mail = {
            idTo: vendedor.idVendedor.toString(),
            idTipo: 2,
            param2: 'Se generó un nuevo pedido',
            param3: `${rtn.idPedido}`
          }

          if (requiereAvisoVendedor) {
            if (requiereImpuestos && requiereEnvioAConsultar) {
              texto =
                'Por favor, revisar el detalle del pedido en Gestionar Pedido antes de continuar.'
            } else if (requiereImpuestos) {
              texto =
                'Se generó un pedido con percepciones. Revisar el detalle de impuestos del pedido.'
            } else if (requiereEnvioAConsultar) {
              texto =
                'Se generó un pedido con dos listas de envío diferentes. Por favor, revisar el costo de envío.'
            }

            const mensaje = {
              idMensajePadre: null,
              idPublicacion: null,
              idPedido: rtn.idPedido,
              idPresupuesto: null,
              idComprador: idUsuario,
              idVendedor: vendedor.idVendedor,
              idUsuarioChat: vendedor.idVendedor,
              idUsuarioEmisor: idUsuario,
              texto: texto,
              privado: true,
              idAdjunto: null
            }
            await mensajesService.insertar(mensaje)
          }
          await insertarMail(mail)
        }
      }

      if (pedidosCreados.length > 0) {
        // Eliminar carrito con todos los items
        for (const pedidoCreado of pedidosCreados) {
          await eliminarCarrito(pedidoCreado)
        }
        const pedidoMostrarModal = pedidosCreados.find((pedido) =>
          pedido.pedidoItems.some((item) => item.idMedioPago === MERCADO_PAGO)
        )
        const mensajeMontoEnvio = pedidosCreados.some((pedido) =>
          pedido.pedidoItems.some(
            (item) =>
              item.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR &&
              item.precioEnvio === 0 &&
              item.idMedioPago === MERCADO_PAGO
          )
        )

        // Mostrar comprobante o navegar
        if (conObservacion && pedidosCreados[0]?.pedidoItems?.length > 0) {
          navigate(`/Pedido/${pedidoMostrarModal.idPedido}`, {
            state: {
              mostrarModalImpuestos: true,
              pedido: checkoutsSeparados.find(
                (checkout) => checkout.checkoutDTO.idPedido === pedidoMostrarModal.idPedido
              )?.checkoutDTO,
              esVentas: false,
              vieneDeCompras: true,
              requiereMensajeMontoEnvio: mensajeMontoEnvio
            }
          })
        } else {
          setPedidos(pedidosCreados)
          setVerComprobante(true)
        }
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al crear el pedido.`
      toastErrorLog(error, mensaje, 'compra')
    }
  }

  const eliminarCarritoItem = async (item) => {
    try {
      const { data: carritoCantidad } = await carritoService.eliminar({
        idPublicacion: item.idPublicacion,
        idUsuario
      })
      setCantidad(carritoCantidad)
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el carrito.`
      toastErrorLog(error, mensaje, 'carrito')
    }
  }

  const eliminarCarrito = async (pedido) => {
    for (const item of pedido.pedidoItems) {
      await eliminarCarritoItem(item)
    }
    const idsEliminados = pedido.pedidoItems.map((item) => item.idPublicacion)
    setItems((prevItems) => prevItems.filter((i) => !idsEliminados.includes(i.idPublicacion)))
  }

  const validaCampos = () => {
    const idsItems = vendedor.pedidoItems.map((pedido) => obtenerIdItem(pedido))

    const itemsConEnvioComprador = vendedor.pedidoItems.filter(
      (pedido) => idMedioEnvio[obtenerIdItem(pedido)] === ENVIO_A_CARGO_COMPRADOR
    )

    if (idsItems.length !== Object.keys(idMedioEnvio).length) {
      return 'Debes seleccionar un medio de envío para todos los ítems'
    } else if (
      vendedor.pedidoItems.filter((pedido) => {
        const idMedio = idMedioEnvio[obtenerIdItem(pedido)]
        return idMedio === ENVIO_GRATIS || idMedio === ENVIO_A_CARGO_COMPRADOR
      }).length !== Object.keys(direccionEntrega).length
    ) {
      return 'Debes seleccionar un domicilio de envío para todos los ítems'
    } else if (
      itemsConEnvioComprador.length > 0 &&
      itemsConEnvioComprador.some((pedido) => {
        const idItem = obtenerIdItem(pedido)
        return !listaEnvioSeleccionada[idItem] && !idsItemConConflictoListas.has(idItem)
      })
    ) {
      return 'Debes seleccionar una lista de envío.'
    } else if (idsItems.length !== Object.keys(medioPagoSeleccionado).length) {
      return 'Debes seleccionar un medio de pago para todos los ítems'
    } else {
      return false
    }
  }

  const construirCheckoutsSeparados = (vendedor, conObservacion = false) => {
    const gruposPorMedioPago = vendedor.pedidoItems.reduce((grupos, item) => {
      const idMedioPago = Number(
        medioPagoSeleccionado[item.idPresupuestoItem || item.idPublicacion]
      )
      grupos[idMedioPago] ??= []
      grupos[idMedioPago].push(item)
      return grupos
    }, {})

    const domicilioPrincipalVendedor = domiciliosVendedor.find((d) => d.domicilioPrincipal === true)

    return Object.values(gruposPorMedioPago).map((itemsDelGrupo) => {
      const totalConIvaGrupo = itemsDelGrupo.reduce(
        (total, item) => total + calcularTotalItemConIva(item),
        0
      )

      const idsItemConConflictoListasGrupo = detectarConflictoListasPorDomicilio(itemsDelGrupo)
      const pedidoItems = itemsDelGrupo.map((item) => {
        const id = obtenerIdItem(item)

        const esRetiraComprador = idMedioEnvio[id] === RETIRA_COMPRADOR
        const domicilio = direccionEntrega[id] || {}

        let idProvinciaFinal = idProvinciaValida(domicilio.idProvincia)
          ? Number(domicilio.idProvincia)
          : null
        if (esRetiraComprador && domicilioPrincipalVendedor?.provincia) {
          idProvinciaFinal =
            provinciasPorNombre.get(normalizarNombre(domicilioPrincipalVendedor.provincia)) ?? null
        }

        const cantidad = Number(item.cantidad)
        let precioUnitarioVisual = item.precioUnitario
        let porcDctoFinal = item.porcDcto

        if (item.valorTotalSinIva && cantidad > 0) {
          precioUnitarioVisual = Number(item.valorTotalSinIva) / cantidad
          porcDctoFinal = 0
        }

        return {
          ...item,
          idPagoEstado:
            Number(medioPagoSeleccionado[id]) === CTA_CTE ? PAGO_EN_CTA_CTE : PAGO_PENDIENTE,
          idProvincia: idProvinciaValida(idProvinciaFinal) ? Number(idProvinciaFinal) : null,
          precioUnitario: precioUnitarioVisual,
          porcDcto: porcDctoFinal,
          entregaProvincia: domicilio.provincia || '',
          entregaLocalidad: domicilio.idLocalidad ? Number(domicilio.idLocalidad) : 0,
          entregaLocalidadDesc: domicilio.localidad || '',
          entregaCP: domicilio.cp || '',
          entregaCalle: domicilio.calle || '',
          entregaCalleAltura: domicilio.calleAltura
            ? String(domicilio.calleAltura).replace(/\D/g, '') || '0'
            : '0',
          entregaPiso: domicilio.piso || '',
          entregaDpto: domicilio.departamento || '',
          idMedioEnvio: Number(idMedioEnvio[id]),
          idMedioPago: Number(medioPagoSeleccionado[id]),
          precioEnvio: idsItemConConflictoListasGrupo.has(id) ? 0 : obtenerPrecioEnvioPorItem(item),
          total: Number(totalConIvaGrupo),
          imputacionTipoCod: item.imputacionTipo || '',
          imputacionTipoDesc: item.imputacionTipoCod || '',
          codMat: item.codMatCod || '',
          descripcionMat: item.codMat || '',
          valorPlantaCod: item.valorPlantaCod || '',
          valorPlantaDesc: item.valorPlanta || '',
          imputacionTipoOpcionCod: item.imputacionTipoOpcionCod || '',
          imputacionTipoOpcionDesc: item.imputacionTipoOpcion || '',
          anticipo: item.anticipo
        }
      })

      const checkoutDTO = {
        ...vendedor,
        idComprador: idUsuario,
        pedidoItems
      }

      const percepcionesDelPedido = calcularPercepciones(itemsDelGrupo)

      const requiereEnvioAConsultar = idsItemConConflictoListasGrupo.size > 0

      const pedidoItemsConEstadoFinal = pedidoItems.map((item) => {
        if (idsItemConConflictoListasGrupo.has(obtenerIdItem(item))) {
          return {
            ...item,
            idPedidoItemEstado: ID_ESTADO_MONTOS_NO_OK
          }
        }

        const estadoCalculado =
          Number(item.idMedioPago) === CTA_CTE
            ? tieneWFCompra === 'S'
              ? ID_ESTADO_NO_CONFIRMADO
              : ID_ESTADO_PEDIDO_CREADO
            : Number(item.idMedioPago) === MERCADO_PAGO
              ? conObservacion
                ? ID_ESTADO_MONTOS_NO_OK
                : percepcionesDelPedido.length > 0
                  ? ID_ESTADO_MONTOS_OK
                  : ID_ESTADO_PENDIENTE_PAGO
              : ID_ESTADO_PEDIDO_CREADO

        return {
          ...item,
          idPedidoItemEstado: estadoCalculado
        }
      })

      return {
        checkoutDTO: {
          ...checkoutDTO,
          pedidoItems: pedidoItemsConEstadoFinal
        },
        percepciones: percepcionesDelPedido,
        requiereEnvioAConsultar
      }
    })
  }

  const handlerContinuar = (vendedor, conObservacion = false) => {
    const error = validaCampos()
    if (error) {
      setError(error)
      return
    }

    crearPedido(construirCheckoutsSeparados(vendedor, conObservacion), conObservacion)
  }

  const obtenerDomiciliosVendedor = async () => {
    const { data } = await domiciliosService.buscarTodas(
      vendedor.idVendedor,
      vendedor.pedidoItems?.[0]?.tipoUsuario
    )
    setDomiciliosVendedor(data || [])
  }

  const idsItems = useMemo(() => {
    return (vendedor?.pedidoItems ?? []).map((i) => obtenerIdItem(i))
  }, [vendedor?.pedidoItems])

  const todosMediosEnvioSeleccionados = useMemo(() => {
    if (idsItems.length === 0) {
      return false
    }
    return idsItems.every((id) => idMedioEnvio[id] !== undefined && idMedioEnvio[id] !== null)
  }, [idsItems, idMedioEnvio])

  const {
    percepciones,
    calcularPercepciones,
    guardarPercepciones,
    setDatosFiscalesComprador,
    setDatosFiscalesVendedor
  } = usePercepciones({
    vendedor,
    empid,
    idMedioEnvio,
    direccionEntrega,
    medioPagoSeleccionado,
    listaEnvioSeleccionada,
    provinciasPorNombre,
    provinciasPorId,
    domiciliosVendedor,
    todosMediosEnvioSeleccionados,
    obtenerPrecioEnvioPorItem,
    normalizarNombre
  })

  const obtenerDatosFiscales = async (empidEnviado) => {
    const { data } = await datosFiscalesService.buscarTodos(empidEnviado)
    if (empidEnviado === empid) {
      setDatosFiscalesComprador(data)
    } else {
      setDatosFiscalesVendedor(data)
    }
  }

  useEffect(() => {
    if (!vendedor) return

    obtenerDatosFiscales(empid)
    obtenerDomiciliosVendedor()
    const empidVendedor = vendedor.pedidoItems?.[0]?.empid
    if (!empidVendedor) {
      return
    }
    obtenerDatosFiscales(empidVendedor)
  }, [empid, vendedor?.pedidoItems])

  if (!vendedor) {
    return null
  }

  const domiciliosPreferidos = domicilios?.filter((d) => d.esPreferido)
  const domiciliosAMostrar =
    domiciliosPreferidos.length > 0 ? domiciliosPreferidos : domicilios || []

  const opciones = domiciliosAMostrar?.map((d) => ({
    ...d,
    value: d.idUsuarioDomicilio,
    label: `${d.tipoDomicilio} - ${[d.calle, d.localidad, d.provincia]
      .filter(Boolean)
      .join(', ')}${d.domicilioPrincipal ? ' (Principal)' : ''}`
  }))

  const total = vendedor.pedidoItems.reduce(
    (total, item) => total + calcularTotalItemConIva(item),
    0
  )

  const totalSinIva = vendedor.pedidoItems.reduce(
    (total, item) => total + calcularTotalItemSinIva(item),
    0
  )

  const detallePrecioProps = {
    pedidoItems: vendedor.pedidoItems,
    valorUnitarioSinIva,
    esMayorista,
    total,
    totalSinIva,
    costoEnvio,
    porcDcto: vendedor.pedidoItems[0]?.porcDcto || 0
  }

  const mostrarAvisoEntrega = (vendedor.pedidoItems ?? []).some(
    (item) =>
      idMedioEnvio[obtenerIdItem(item)] === ENVIO_GRATIS ||
      idMedioEnvio[obtenerIdItem(item)] === ENVIO_A_CARGO_COMPRADOR
  )

  if (verComprobante) {
    return pedidos.map((pedido) => {
      const muestraPasarela = pedido.pedidoItems.some(
        (item) => medioPagoSeleccionado[item.idPublicacion] === MERCADO_PAGO
      )

      return (
        <Comprobante
          key={pedido.idPedido}
          vendedor={vendedor}
          pedido={pedido}
          medioPago={medioPagoSeleccionado}
          muestraPasarela={muestraPasarela}
        />
      )
    })
  }

  return (
    <div className={styles.page}>
      <div className={styles.checkoutGrid}>
        <div className={styles.itemsColumn}>
          <div className={styles.header}>
            <h1 className={styles.title}>Confirmá tu pedido</h1>
            <p className={styles.subtitle}>Revisá la entrega y el medio de pago de cada producto</p>
          </div>
          <h2 className={styles.sectionTitle}>Productos</h2>

          <div className={styles.itemsList}>
            {vendedor.pedidoItems.map((item) => {
              const clave = item.idPresupuestoItem || item.idPublicacion
              const opcionesMP = opcionesMedioPago(mediosPagosPorItem.get(clave))
              const mostrarDomicilio =
                idMedioEnvio[clave] === ENVIO_A_CARGO_COMPRADOR ||
                idMedioEnvio[clave] === ENVIO_GRATIS
              const mostrarListaEnvioItem = idMedioEnvio[clave] === ENVIO_A_CARGO_COMPRADOR

              return (
                <div className={styles.itemCard} key={clave}>
                  <div className={styles.itemCardHeader}>
                    <div className={styles.itemInfo}>
                      <span className={styles.itemTitulo} title={item.titulo}>
                        {item.titulo}
                      </span>
                      <span className={styles.itemCantidad}>
                        {item.cantidad} {Number(item.cantidad) === 1 ? 'unidad' : 'unidades'}
                      </span>
                    </div>
                    <div className={styles.itemPrecio}>
                      <Precios
                        precio={valorUnitarioSinIva(item)}
                        monedaSimbolo={item.monedaSimbolo}
                        alicuota={item.alicuota}
                        cantidad={item.cantidad}
                        sufijo={esMayorista(item) ? '(mayorista)' : ''}
                        porcDcto={item.porcDcto}
                      />
                    </div>
                  </div>

                  <hr className={styles.itemDivider} />

                  <div className={styles.itemField}>
                    <span className={styles.fieldLabel}>Tipo de entrega</span>
                    <div className={styles.segmentedControl}>
                      {mediosEnvios
                        .filter((opcion) => item.mediosEnvios.includes(opcion.value.toString()))
                        .map((opcion) => {
                          const inputId = `idMedioEnvio-${clave}-${opcion.value}`
                          return (
                            <div className={styles.segmentOption} key={opcion.value}>
                              <input
                                type="radio"
                                id={inputId}
                                className={styles.segmentInput}
                                name={`idMedioEnvio-${clave}`}
                                checked={idMedioEnvio[clave] === opcion.value}
                                onChange={() => handleClickMedioEnvio(opcion.value, clave)}
                              />
                              <label htmlFor={inputId} className={styles.segmentLabel}>
                                {opcion.label}
                              </label>
                            </div>
                          )
                        })}
                    </div>
                  </div>

                  <div className={styles.selectsRow}>
                    {mostrarDomicilio && (
                      <div className={styles.itemField}>
                        <span className={styles.fieldLabel}>Domicilio de envío</span>
                        <div className={`d-flex align-items-center ${styles.domicilioWrapper}`}>
                          <Select
                            key={domicilios.length}
                            options={opciones}
                            value={opciones.find(
                              (o) =>
                                o.value ===
                                (direccionEntrega[item.idPublicacion]?.idUsuarioDomicilio || null)
                            )}
                            onChange={(selected) => handleClickDomicilioEnvio(selected, clave)}
                            isSearchable
                            placeholder="Selecciona un domicilio"
                            className={styles.selectWrapper}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={selectMenuPortalStyles}
                          />
                          {tipoUsuario && puedeAgregarDomicilio(tipoUsuario) && (
                            <div
                              className={styles.addButton}
                              style={{ cursor: 'pointer' }}
                              title="Agregar nuevo domicilio"
                              onClick={handleAbrirModalNuevoDomicilio}
                            >
                              +
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {mostrarListaEnvioItem &&
                      (() => {
                        const estado = obtenerEstadoCelda(item)

                        if (estado.tipo === 'consultar') {
                          return (
                            <div className={styles.itemField}>
                              <span className={styles.fieldLabel}>Lista de envío</span>
                              <button
                                type="button"
                                className={`btn btn-warning btn-sm ${styles.consultarBtn}`}
                                onClick={() => navigate(`/Publicacion/${item.idPublicacion}`)}
                              >
                                Consultar al vendedor
                                <small>{estado.motivo}</small>
                              </button>
                            </div>
                          )
                        }

                        return (
                          <div className={styles.itemField}>
                            <span className={styles.fieldLabel}>Lista de envío</span>
                            <Select
                              options={estado.opciones}
                              value={
                                listaEnvioSeleccionada[
                                  item.idPresupuestoItem || item.idPublicacion
                                ] || null
                              }
                              onChange={(selected) =>
                                handleClickListaEnvio(
                                  selected,
                                  item.idPresupuestoItem || item.idPublicacion
                                )
                              }
                              isSearchable
                              placeholder="Selecciona una lista de envío"
                              className={styles.selectWrapper}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={selectMenuPortalStyles}
                            />
                            {estado.tipo === 'selectConAviso' && (
                              <small
                                className={styles.listaEnvioAviso}
                                title="Los productos con el mismo domicilio y medio de pago tienen listas de envío diferentes. El vendedor definirá el costo de envío al gestionar el pedido."
                              >
                                {estado.aviso}
                              </small>
                            )}
                          </div>
                        )
                      })()}

                    <div className={styles.itemField}>
                      <span className={styles.fieldLabel}>Medio de pago</span>
                      <Select
                        options={opcionesMP}
                        value={
                          opcionesMP.find(
                            (o) => o.value === (medioPagoSeleccionado?.[clave] ?? null)
                          ) || null
                        }
                        onChange={(selected) => {
                          setMedioPagoSeleccionado((prev) => ({
                            ...prev,
                            [clave]: selected ? selected.value : null
                          }))
                          setError('')
                        }}
                        placeholder="Selecciona un medio"
                        className={styles.selectWrapper}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectMenuPortalStyles}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {error && (
            <div className={styles.formError} role="alert">
              {error}
            </div>
          )}
        </div>

        <div className={styles.summaryColumn}>
          <div className={styles.summarySticky}>
            <div className={styles.summaryCard}>
              <DetallePrecio
                {...detallePrecioProps}
                usaImagen={false}
                titulo="Resumen del pedido"
                costoEnvio={costoEnvio}
                pedidoCompletoEsCtaCte={pedidoCompletoEsCtaCte}
                percepciones={percepciones}
              />
            </div>
            {mostrarAvisoEntrega && (
              <div className={styles.avisoEntrega}>
                <i className={styles.avisoEntregaIcono} aria-hidden="true">
                  i
                </i>
                <div className={styles.avisoEntregaContenido}>
                  <strong>Importante</strong>
                  Tu dirección de entrega y las condiciones seleccionadas deberán coincidir con los
                  destinos y costos ofrecidos. De lo contrario, el vendedor podrá{' '}
                  <strong style={{ display: 'inline' }}>rechazar</strong> el pedido.
                </div>
              </div>
            )}
            <div className={styles.actions}>
              <div className={styles.actionsRow}>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => handlerContinuar(vendedor)}
                >
                  Confirmar operación
                </button>
                {Object.values(medioPagoSeleccionado).includes(MERCADO_PAGO) && (
                  <button
                    type="button"
                    className={styles.btnTertiary}
                    onClick={() => handlerContinuar(vendedor, true)}
                  >
                    Confirmar operación con observación de impuestos
                  </button>
                )}
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => {
                    resetListaEnvioSeleccionada()
                    navigate(-1)
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalDomicilio
        show={verModalNuevoDomicilio}
        domicilios={domicilios}
        onClose={() => setVerModalNuevoDomicilio(false)}
        onDomicilioAgregado={handleDomicilioAgregado}
      />
    </div>
  )
}

export default Compra
